import useCurrentView from '@/composables/saved-views/current-view';
import useDefaultView from '@/composables/saved-views/default-view';
import useEnrichParams from '@/composables/saved-views/enrich-params';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useSelectedViews from '@/composables/saved-views/selected-views';
import useViewAccess from '@/composables/saved-views/view-access';
import useViewCollections from '@/composables/saved-views/view-collections';
import useViews from '@/composables/saved-views/views';
import useViewsContext from '@/composables/saved-views/views-context';
import useViewsRepo from '@/composables/saved-views/views-repo';
import useViewsRepoLocal from '@/composables/saved-views/views-repo-local';
import { computed } from 'vue';
import { findViews } from '@/lib/saved-view/saved-view';
import { useRoute, useRouter } from 'vue-router';

export default function useRouteAwareViews({
  defaultProps,
  defaultView,
  application,
  space = null,
  planning = null,
  user = null,
  readOnly,
}) {
  const router = useRouter();
  const route = useRoute();
  const { loggedInUserAccount } = useLoggedInUserAccount();
  const { loggedInUser } = useLoggedInUser();
  const selectedViewsSvc = useSelectedViews();

  const context = useViewsContext(application, readOnly);

  const viewCollectionSvc = useViewCollections();
  const viewCollection = computed(() => {
    const viewCollections = findViews({
      views: viewCollectionSvc.viewCollections.value,
      application,
      space,
      planning,
      user,
    });
    if (viewCollections.length === 0) {
      return null;
    }
    return viewCollections[0];
  });

  const viewsRepo = useViewsRepo({
    application,
    space,
    planning,
    user,
  });
  const defaultViewRepo = useDefaultView(
    defaultView,
    defaultProps,
    application,
    space,
    planning,
    user,
    loggedInUser,
    loggedInUserAccount,
    null,
    viewsRepo,
    viewCollection,
  );
  const viewsRepoLocal = useViewsRepoLocal(defaultViewRepo, loggedInUserAccount);
  const viewAccessRepo = useViewAccess(viewsRepoLocal, context);
  const repo = useEnrichParams(viewAccessRepo);

  const selectedViewForApplication = selectedViewsSvc.getSelectedViewForApplication({
    application,
    space,
    planning,
    user,
  });

  const changeSelectedView = (view) => {
    router.push({ query: { ...route.query, viewId: view.uid } });
    return selectedViewsSvc.saveOrUpdateSelectedView(selectedViewForApplication.value, view.uid);
  };

  const viewsSvc = useViews({
    application,
    space,
    planning,
    user,
    selectedViewsSvc,
    changeSelectedView,
    repo,
    viewCollection,
  });

  const currentViewSvc = useCurrentView(
    defaultProps,
    viewsSvc,
    selectedViewForApplication,
  );

  const parseViewId = (routeViewId) => {
    if (typeof routeViewId === 'undefined') {
      return 0;
    }
    const viewId = parseInt(routeViewId, 10);
    return Number.isNaN(viewId) ? 0 : viewId;
  };

  const routeViewId = computed(() => {
    if (readOnly) {
      return parseViewId(route.params.savedViewId);
    }
    return parseViewId(route.query.viewId);
  });

  if (routeViewId.value !== 0 && viewsSvc.selectedView.value.view.uid !== routeViewId.value.uid) {
    selectedViewsSvc.saveOrUpdateSelectedView(selectedViewForApplication.value, routeViewId.value);
  }

  return {
    createLoading: viewsSvc.createLoading,
    createViews: viewsSvc.createViews,

    loadingUpdateView: viewsSvc.loadingUpdateView,
    updateView: viewsSvc.updateView,

    loadingDeleteView: viewsSvc.loadingDeleteView,
    deleteView: viewsSvc.deleteView,

    setParams: viewsSvc.setParams,
    updateSort: viewsSvc.updateSort,
    updateCycles: viewsSvc.updateCycles,
    updateLockCyclesOnView: viewsSvc.updateLockCyclesOnView,

    currentViewHasUnsavedChanges: currentViewSvc.hasUnsavedChanges,
    currentViewUnsavedChanges: currentViewSvc.unsavedChanges,

    resetView: viewsSvc.resetView,

    views: viewsSvc.views,
    viewCollection: viewsSvc.viewCollection,

    selectSingle: viewsRepo.selectSingle,

    currentView: currentViewSvc.currentView,
    context,

    changeSelectedView,

    initCurrentView: viewsSvc.initCurrentView,
  };
}
